import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

const formatDateTime = (value) => {
    if (!value) {
        return '---';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '---';
    }

    return date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatFileSize = (bytes) => {
    if (!Number.isFinite(bytes) || bytes < 0) {
        return '---';
    }

    if (bytes < 1024) {
        return `${bytes} B`;
    }

    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }

    if (bytes < 1024 * 1024 * 1024) {
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

export default function ClientContractSection({
    title,
    contracts,
    selectedFiles,
    onFileChange,
    onUpload,
    onDownload,
    onPreview,
    onDelete,
    isLoading = false,
    isUploading = false,
    disabled = false,
    disabledText = '',
    emptyText = '',
    canDelete = true,
}) {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    return (
        <div className="border-t border-[var(--border)] pt-5 space-y-4">
            <div>
                <h4 className="text-sm font-semibold text-[var(--text-primary)]">{title}</h4>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                    {disabled ? disabledText : t('clientManagement.contractSectionHint')}
                </p>
            </div>

            {!disabled && (
                <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--background)] p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            onChange={(event) => {
                                onFileChange(Array.from(event.target.files || []));
                                event.target.value = '';
                            }}
                            className="hidden"
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="rounded-full bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                        >
                            {t('selectFiles')}
                        </button>
                        <span className="min-w-0 flex-1 truncate text-sm text-[var(--text-secondary)]">
                            {selectedFiles.length > 0
                                ? t('clientManagement.contractFilesSelected', { count: selectedFiles.length })
                                : t('noFilesSelected')}
                        </span>
                        <button
                            type="button"
                            onClick={onUpload}
                            disabled={isUploading || selectedFiles.length === 0}
                            className="btn-base bg-blue-600 text-white hover:bg-blue-700 shadow-md disabled:bg-blue-300 whitespace-nowrap"
                        >
                            {isUploading ? t('processing') : t('clientManagement.uploadContracts')}
                        </button>
                    </div>
                    {selectedFiles.length > 0 && (
                        <p className="mt-3 text-xs text-[var(--text-secondary)]">
                            {t('clientManagement.contractFilesSelected', { count: selectedFiles.length })}
                        </p>
                    )}
                </div>
            )}

            {disabled ? (
                <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--background)] p-4 text-sm text-[var(--text-secondary)]">
                    {disabledText}
                </div>
            ) : isLoading ? (
                <p className="text-sm text-[var(--text-secondary)]">{t('loading')}</p>
            ) : contracts.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--background)] p-4 text-sm text-[var(--text-secondary)]">
                    {emptyText}
                </div>
            ) : (
                <ul className="space-y-3">
                    {contracts.map((contract) => (
                        <li
                            key={contract.id}
                            className="rounded-2xl border border-[var(--border)] bg-[var(--background)] p-4"
                        >
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                <div className="min-w-0">
                                    <div className="font-medium text-[var(--text-primary)] break-all">{contract.fileName}</div>
                                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--text-secondary)]">
                                        <span>{t('clientManagement.uploadedBy')}: {contract.uploadedByUsername || '---'}</span>
                                        <span>{t('clientManagement.uploadedAt')}: {formatDateTime(contract.createdAt)}</span>
                                        <span>{t('clientManagement.fileSize')}: {formatFileSize(contract.fileSize)}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => onDownload(contract.id, contract.fileName)}
                                        className="px-3 py-2 text-sm font-semibold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
                                    >
                                        {t('downloadAttachment')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => onPreview(contract.id, contract.fileName, contract.fileType)}
                                        className="px-3 py-2 text-sm font-semibold rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100"
                                    >
                                        {t('viewOnline')}
                                    </button>
                                    {canDelete && (
                                        <button
                                            type="button"
                                            onClick={() => onDelete(contract.id)}
                                            className="px-3 py-2 text-sm font-semibold rounded-lg bg-red-50 text-red-700 hover:bg-red-100"
                                        >
                                            {t('deleteAttachment')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
